/*
 * /kb/id/{articleId} - a permalink that does not depend on an address.
 *
 * Article ids are stable; an article's readable address is not, and now moves in
 * two ways rather than one - a re-slug, and a move to another product. A link
 * written against the id survives both, which is also why the article picker in
 * the editor inserts the id form of the [[kb:...]] token.
 *
 * The route resolves the id and then replaces itself with the canonical
 * /kb/{productSlug}/{slug} URL, so what ends up in the address bar and in the
 * reader's history is the readable form. The product half comes from the
 * response rather than from anything this page knows: the id lookup is the only
 * thing that can say which product an article currently belongs to, which is
 * precisely why the lookup still returns it. `replace` rather than a push: a
 * back button that returned to a redirect would bounce the reader forwards again.
 */

import { Navigate, useParams } from 'react-router-dom';
import { articleHref } from '@accotool-kb/shared';
import { getArticleById } from '../api/publicApi.js';
import { forReader } from '../api/publicReader.js';
import { usePublicReader } from '../auth/AuthProvider.js';
import { ApiError } from '../api/client.js';
import { useAsync } from '../hooks/useAsync.js';
import { ErrorBanner, Loading } from '../components/ui.js';
import { SignInHint } from './SignInHint.js';

export function ArticleByIdPage(): JSX.Element {
  const params = useParams();
  const articleId = params.articleId ?? '';

  // As the reader (S46): a permalink to a private article resolves for its
  // audiences, and is the plain 404 - with the sign-in hint - for anybody else.
  const reader = usePublicReader();
  const state = useAsync(
    () => forReader(reader, () => getArticleById(articleId, reader)),
    [articleId, reader.ready, reader.key],
  );

  if (state.loading && !state.data) return <Loading />;
  if (state.error) {
    return (
      <>
        <ErrorBanner error={state.error} />
        {state.error instanceof ApiError && state.error.status === 404 && <SignInHint />}
      </>
    );
  }
  if (!state.data) return <Loading />;

  return <Navigate to={articleHref(state.data.product.slug, state.data.slug)} replace />;
}
